/**
 * CSV export for PACE → Allocations ("Download selected" / "Download loaded").
 *
 * Built entirely from rows already in the browser — no API call. Every record the
 * grid has loaded carries its full v2_ranked_json, which is what the per-order
 * "Why this ranking" explainer renders, so the export can carry everything the UI
 * shows (grid columns + every courier card) without fetching anything.
 *
 * Shape: long, one row per (allocation × courier considered). The allocation's own
 * columns repeat on each of its courier rows so a single file pivots cleanly.
 *  - one row per allocation  → filter `first_row_of_allocation = 1`
 *  - PACE's pick only        → filter `is_pace_pick = 1`
 *  - an error / no ranked data allocation still gets one row, courier columns blank.
 * Columns are fixed (not inferred from the data) so the header is stable across
 * downloads and analyst templates don't break.
 *
 * Rates are exported as percentages (0–100, as the UI shows them); money in ₹.
 */

// ── CSV ────────────────────────────────────────────────────────────────────
const NUMERIC = /^-?\d+(\.\d+)?(e[+-]?\d+)?$/i

const cell = (v) => {
  if (v === null || v === undefined) return ''
  const s = String(v)
  // Guard against CSV formula injection in Excel/Sheets — but never on numbers, or
  // negative deltas would turn into text.
  const safe = typeof v !== 'number' && !NUMERIC.test(s) && /^[=+\-@\t\r]/.test(s) ? `'${s}` : s
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
}

function rowsToCsv(columns, rows) {
  const out = [columns.join(',')]
  for (const r of rows) out.push(columns.map((c) => cell(r[c])).join(','))
  return `${out.join('\n')}\n`
}

export function downloadCsv(filename, text) {
  // BOM so Excel reads ₹ / Δ / merchant names as UTF-8.
  const url = URL.createObjectURL(new Blob(['﻿', text], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ── helpers ────────────────────────────────────────────────────────────────
const val = (v) => (v === null || v === undefined || v === '' ? '' : v)
const pct = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v))
  ? '' : Math.round(Number(v) * 10000) / 100)
const round = (v, d = 2) => (v === null || v === undefined || Number.isNaN(Number(v))
  ? '' : Math.round(Number(v) * 10 ** d) / 10 ** d)
const flag = (b) => (b === null || b === undefined ? '' : (b ? 1 : 0))

// Service type the order asked for (SDD / NDD / …) — PACE echoes it in the response meta.
export function requiredServiceType(r) {
  try {
    const m = JSON.parse(r.v2ResponseMeta || '{}')
    return (m && m.required_service_type) || null
  } catch (e) {
    return null
  }
}

export function rankedOf(r) {
  try {
    const v = JSON.parse(r.v2RankedJson || '[]')
    return Array.isArray(v) ? v : []
  } catch (e) {
    return []
  }
}

// ── columns ────────────────────────────────────────────────────────────────
// Allocation level — the grid row, plus the stored fields the grid doesn't have room for.
const ALLOCATION_COLS = [
  'allocation_id', 'created_at', 'merchant', 'merchant_id', 'merchant_oid', 'reference', 'awb',
  'mode', 'service_asked', 'pickup_pincode', 'drop_pincode',
  'rules_pick', 'pace_pick', 'pace_pick_prozo_id', 'agree_top1',
  'pace_score', 'pace_cost', 'pace_eta_h', 'pace_p_delivered_%', 'pace_p_rto_%', 'pace_p_first_attempt_ok_%',
  'delta_cost_rules_minus_pace', 'delta_time_h_rules_minus_pace',
  'couriers_considered', 'rules_pick_rank_in_pace', 'score_gap_top1_vs_top2',
  'kci_couriers_delayed', 'error',
]

// Courier level — every number on a courier card in the "Why this ranking" explainer.
const COURIER_COLS = [
  'first_row_of_allocation', 'courier_rank', 'courier', 'courier_service_type', 'courier_tier',
  'is_pace_pick', 'is_rules_pick',
  'available', 'unavailable_reason', 'score', 'quality_source',
  // unified quality factors: value → weight → contribution, and share of the score (the pie)
  'delivery_%', 'delivery_weight', 'delivery_contribution', 'delivery_share_%',
  'fasr_%', 'fasr_weight', 'fasr_contribution', 'fasr_share_%',
  'fata_%', 'fata_weight', 'fata_contribution', 'fata_share_%',
  // logistics bucket: α-blended speed/cost penalty, soft-capped to L
  'logistics_L', 'logistics_weight', 'logistics_contribution', 'logistics_share_%',
  'logistics_eta_h', 'logistics_cost', 'alpha', 'logistics_penalty', 'logistics_cap',
  // ML head probabilities + ETA
  'p_delivered_%', 'p_rto_%', 'p_first_attempt_ok_%', 'est_transit_h',
  // cost split: rate-card + WD surcharge + risk (applied) = total
  'quote', 'cost_rate_card', 'cost_wd_surcharge', 'cost_real',
  'risk_rto_raw', 'risk_reattempt_raw', 'risk_lost_raw', 'risk_raw_total', 'risk_weight',
  'cost_risk_applied', 'cost_total', 'est_total_cost',
  // chargeable weight (WD-adjusted vs declared)
  'wd_adjusted', 'declared_weight_g', 'wd_adjusted_weight_g', 'wd_added_g', 'wd_source',
  'chargeable_weight_g',
  // weighted-scorecard / speed↔cost-split explainers (older stored rows; blank under unified)
  'scorecard_grain', 'scorecard_support_n',
  'scorecard_delivery_wilson_%', 'scorecard_fasr_wilson_%', 'scorecard_fata_wilson_%',
  'alpha_speed_term', 'alpha_cost_term',
  // KCI disruption (PACE decision/kci.py): delay_h > 0 with applied 0 = shadow (kci_enabled off)
  'kci_matched_events', 'kci_delay_h', 'kci_applied', 'kci_base_eta_h', 'kci_legs', 'kci_events',
]

export const EXPORT_COLUMNS = [...ALLOCATION_COLS, ...COURIER_COLS]

// ── row builders ───────────────────────────────────────────────────────────
function allocationFields(r, merchantName, ranked) {
  const liveIdx = r.v1TopAccountCode
    ? ranked.findIndex((c) => c && c.prozo_id === r.v1TopAccountCode) : -1
  const s1 = ranked[0]?.score
  const s2 = ranked[1]?.score
  return {
    allocation_id: val(r.id),
    created_at: val(r.createdAt),
    merchant: val(merchantName),
    merchant_id: val(r.merchantId),
    merchant_oid: val(r.merchantOid),
    reference: val(r.reference),
    awb: val(r.awb),
    mode: val(r.mode),
    service_asked: val(requiredServiceType(r)),
    pickup_pincode: val(r.pickupPincode),
    drop_pincode: val(r.dropPincode),
    rules_pick: val(r.v1TopAccountCode),
    pace_pick: r.v2TopAccountCode || (r.error ? 'error' : ''),
    pace_pick_prozo_id: val(r.v2TopProzoId),
    agree_top1: r.mode === 'SHADOW' ? (r.agreeTop1 ? 'YES' : 'NO') : '',
    pace_score: val(r.v2Score),
    pace_cost: val(r.v2EstTotalCost),
    pace_eta_h: val(r.v2EstTransitHours),
    'pace_p_delivered_%': pct(r.v2PDelivered),
    'pace_p_rto_%': pct(r.v2PRto),
    'pace_p_first_attempt_ok_%': pct(r.v2PFirstAttemptOk),
    delta_cost_rules_minus_pace: val(r.deltaCost),
    delta_time_h_rules_minus_pace: val(r.deltaTransitHours),
    couriers_considered: ranked.length,
    kci_couriers_delayed: ranked.filter((c) => c && c.kci && c.kci.delay_h > 0).length,
    // Where the rules' pick sits in PACE's list: 1 = agree, blank = not in PACE's list
    // (filtered by the delivery guardrail, or LIVE rows with no rules pick).
    rules_pick_rank_in_pace: liveIdx >= 0 ? liveIdx + 1 : '',
    score_gap_top1_vs_top2: s1 != null && s2 != null ? round(s1 - s2, 4) : '',
    error: val(r.error),
  }
}

function courierFields(c, i, r) {
  const u = c.unified || {}
  const L = u.logistics || {}
  const cb = c.cost_breakdown || {}
  const sc = c.scorecard || {}
  const bt = c.balanced_terms || {}
  const k = c.kci || null
  const contrib = ['delivery', 'fasr', 'tat', 'logistics'].map((k) => Number(u[k]?.contribution) || 0)
  const contribSum = contrib.reduce((a, b) => a + b, 0)
  const share = (k) => (u[k]?.contribution == null || !contribSum
    ? '' : round((Number(u[k].contribution) / contribSum) * 100, 1))
  const hasRisk = ['rto', 'reattempt', 'lost'].some((k) => cb[k] != null)
  const rawRisk = (cb.rto || 0) + (cb.reattempt || 0) + (cb.lost || 0)
  const chargeable = c.wd_adjusted && c.wd_adjusted_weight_g != null
    ? c.wd_adjusted_weight_g : c.declared_weight_g
  return {
    first_row_of_allocation: i === 0 ? 1 : 0,
    courier_rank: c.available === false ? '' : i + 1,
    courier: val(c.prozo_id),
    // vs the service asked: required / fallback (slower) / last_resort (faster) / unknown
    courier_service_type: val(c.service_type),
    courier_tier: c.service_tier && c.service_tier !== 'no_tier' ? c.service_tier : '',
    is_pace_pick: flag(i === 0 && c.available !== false),
    is_rules_pick: flag(!!r.v1TopAccountCode && c.prozo_id === r.v1TopAccountCode),
    available: c.available === undefined ? '' : flag(c.available),
    unavailable_reason: val(c.unavailable_reason),
    score: val(c.score),
    quality_source: val(u.quality_source),
    'delivery_%': pct(u.delivery?.value),
    delivery_weight: val(u.delivery?.weight),
    delivery_contribution: val(u.delivery?.contribution),
    'delivery_share_%': share('delivery'),
    'fasr_%': pct(u.fasr?.value),
    fasr_weight: val(u.fasr?.weight),
    fasr_contribution: val(u.fasr?.contribution),
    'fasr_share_%': share('fasr'),
    'fata_%': pct(u.tat?.value),
    fata_weight: val(u.tat?.weight),
    fata_contribution: val(u.tat?.contribution),
    'fata_share_%': share('tat'),
    logistics_L: val(L.l_good),
    logistics_weight: val(L.weight),
    logistics_contribution: val(L.contribution),
    'logistics_share_%': share('logistics'),
    logistics_eta_h: val(L.eta_h),
    logistics_cost: val(L.cost),
    alpha: val(L.alpha),
    logistics_penalty: val(L.penalty),
    logistics_cap: val(L.cap),
    'p_delivered_%': pct(c.p_delivered),
    'p_rto_%': pct(c.p_rto),
    'p_first_attempt_ok_%': pct(c.p_first_attempt_ok),
    est_transit_h: val(c.est_transit_hours),
    quote: val(c.quote),
    cost_rate_card: val(cb.quote),
    cost_wd_surcharge: val(cb.surcharge),
    cost_real: val(cb.real_cost),
    risk_rto_raw: val(cb.rto),
    risk_reattempt_raw: val(cb.reattempt),
    risk_lost_raw: val(cb.lost),
    risk_raw_total: hasRisk ? round(rawRisk) : '',
    risk_weight: val(cb.risk_weight),
    // Same fallback the cost waterfall uses: rows stored before risk_weight ⇒ ×1.
    cost_risk_applied: cb.risk_cost != null ? cb.risk_cost
      : (hasRisk ? round(rawRisk * (cb.risk_weight != null ? cb.risk_weight : 1)) : ''),
    cost_total: val(cb.total),
    est_total_cost: val(c.est_total_cost),
    wd_adjusted: c.wd_adjusted === undefined ? '' : flag(c.wd_adjusted),
    declared_weight_g: val(c.declared_weight_g),
    wd_adjusted_weight_g: val(c.wd_adjusted_weight_g),
    wd_added_g: c.wd_added_g != null ? c.wd_added_g
      : (c.wd_adjusted && c.wd_adjusted_weight_g != null && c.declared_weight_g != null
        ? round(c.wd_adjusted_weight_g - c.declared_weight_g) : ''),
    wd_source: val(c.wd_source),
    chargeable_weight_g: val(chargeable),
    scorecard_grain: val(sc.grain),
    scorecard_support_n: val(sc.support_n),
    'scorecard_delivery_wilson_%': pct(sc.delivery?.wilson),
    'scorecard_fasr_wilson_%': pct(sc.fasr?.wilson),
    'scorecard_fata_wilson_%': pct(sc.tat?.wilson),
    alpha_speed_term: val(bt.fast_weighted),
    alpha_cost_term: val(bt.cheap_weighted),
    kci_matched_events: k ? val(k.matched_events) : '',
    kci_delay_h: k ? val(k.delay_h) : '',
    kci_applied: k ? flag(k.applied) : '',
    kci_base_eta_h: k ? val(k.base_eta_h) : '',
    kci_legs: k ? [...new Set((k.events || []).flatMap((e) => e.legs || []))].join('+') : '',
    kci_events: k ? (k.events || []).map((e) => `${e.title || e.event_id} (+${e.delay_h}h)`).join(' | ') : '',
  }
}

/** All CSV rows for one allocation: one per courier considered, or one bare row. */
export function exportRowsFor(r, merchantName) {
  const ranked = rankedOf(r)
  const base = allocationFields(r, merchantName, ranked)
  if (!ranked.length) return [{ ...base, first_row_of_allocation: 1 }]
  return ranked.map((c, i) => ({ ...base, ...courierFields(c || {}, i, r) }))
}

/** Build + download the CSV. Returns the number of CSV rows written. */
export function downloadAllocationsCsv(filename, records, merchantLabel) {
  const rows = records.flatMap((r) => exportRowsFor(r, merchantLabel(r)))
  downloadCsv(filename, rowsToCsv(EXPORT_COLUMNS, rows))
  return rows.length
}
